import { ReactNode, useCallback, useMemo, useState } from 'react';

import { styled, useTheme } from '@mui/material/styles';

import { ActionLabels } from '@sbv-clients/util-misc';

import { Box, Button, DialogContent, Icon, useToggleModal } from '../../index';
import { Dialog, DialogHeader, DialogActionsContainer } from '../dialog/dialog';

export const contentStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  marginTop: 6,
};

const StyledWarningDialog = styled(Dialog)(({ theme }) => {
  return {
    '& .MuiDialog-paper': {
      backgroundColor: theme.palette.background.paper,
    },
    '& .MuiDialogTitle-root': {
      fontWeight: 600,
      fontSize: '18px',
      padding: '16px 16px 8px 16px',
      color: theme.palette.text.primary,
    },
    '& .custom-content': {
      padding: theme.spacing(2),
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '1rem',
      color: theme.palette.text.secondary,
    },
  };
});

export const useWarningModal = (
  title: string,
  content: string,
  sx?: Record<string, string>
) => {
  const { open, openModal, closeModal } = useToggleModal();
  const [errorMessage, setErrorMessage] = useState<ReactNode>(null);
  const setError = (message: ReactNode) => {
    setErrorMessage(message);
  };
  const theme = useTheme();

  const modal = useMemo(() => {
    return (
      <StyledWarningDialog open={open} sx={sx}>
        <DialogHeader
          title={title}
          icon={<Icon name={'WarningSVGOutlined'} />}
          onClose={closeModal}
        />
        <DialogContent sx={contentStyle}>
          <Box display={'flex'} gap={theme.spacing(4)}>
            {content}
          </Box>
          {errorMessage && (
            <div style={{ whiteSpace: 'pre-line' }}>{errorMessage}</div>
          )}
        </DialogContent>
        <DialogActionsContainer>
          <Button variant="outlined" onClick={closeModal}>
            {ActionLabels.Okay}
          </Button>
        </DialogActionsContainer>
      </StyledWarningDialog>
    );
  }, [closeModal, content, errorMessage, open, sx, theme, title]);

  return {
    modal,
    openModal: useCallback(
      (message?: string) => {
        setError(message);
        openModal();
      },
      [openModal]
    ),
    setError,
  };
};
